import React, { useState, useEffect, useRef } from 'react';
import { WATCHES, fmt } from '../data';

const TICKER_WATCHES = [
  { id: 'rolex-gmt-pepsi-126710blnr-2022',     base: 38000 },
  { id: 'rolex-sub-starbucks-126610lv-2022',   base: 12800 },
  { id: 'patek-nautilus-blue-5711-2020',       base: 111000 },
  { id: 'patek-nautilus-olive-5711-2021',      base: 295000 },
  { id: 'rolex-daytona-panda-116500ln-2021',   base: 29500 },
  { id: 'ap-roa-jumbo-15202st-2021',           base: 65000 },
  { id: 'ap-roa-blue-15500st-2023',            base: 34000 },
  { id: 'rm-rm052-skull-2017',                 base: 580000 },
  { id: 'rm-rm011-ti-2019',                    base: 165000 },
  { id: 'rolex-gmt-sprite-126720vtnr-2022',    base: 19500 },
  { id: 'lange-datograph-white-405035-2021',   base: 82000 },
  { id: 'patek-aquanaut-khaki-5168g-2020',     base: 91000 },
  { id: 'rolex-daytona-johnmayer-116508-2018', base: 62000 },
  { id: 'omega-speedy-moonwatch-2022',         base: 6800 },
  { id: 'hublot-bigbang-sapphire-2020',        base: 32000 },
];

function initPrices() {
  return TICKER_WATCHES.map(tw => {
    const w = WATCHES.find(x => x.id === tw.id);
    const name = w ? (w.nickname ? `${w.brand} "${w.nickname}"` : `${w.brand} ${w.model}`) : tw.id;
    const ref = w ? w.ref : '';
    const jitter = 1 + (Math.random() - 0.5) * 0.006;
    const price = Math.round(tw.base * jitter);
    return { id: tw.id, watch: w || null, name, ref, price, base: tw.base, prev: price, pct: 0 };
  });
}

export default function MarketTicker({ onSelectWatch }) {
  const [prices, setPrices] = useState(initPrices);
  const [history, setHistory] = useState(() =>
    TICKER_WATCHES.map(tw => ({
      id: tw.id,
      points: Array.from({ length: 40 }, (_, i) => {
        const drift = 1 + (i - 20) * 0.0008 + (Math.random() - 0.5) * 0.004;
        return Math.round(tw.base * drift);
      }),
    }))
  );
  const tickRef = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      tickRef.current += 1;
      setPrices(prev => prev.map(item => {
        const momentum = (Math.random() - 0.49) * 0.003;
        const newPrice = Math.round(item.price * (1 + momentum));
        const pct = ((newPrice - item.base) / item.base) * 100;
        return { ...item, prev: item.price, price: newPrice, pct };
      }));
      setHistory(prev => prev.map((h, i) => ({
        ...h,
        points: [...h.points.slice(1), prices[i]?.price || h.points[h.points.length - 1]],
      })));
    }, 1800);
    return () => clearInterval(interval);
  }, [prices]);

  const W = 80, H = 32;

  function sparkline(pts) {
    if (!pts || pts.length < 2) return '';
    const min = Math.min(...pts);
    const max = Math.max(...pts);
    const range = max - min || 1;
    return pts.map((p, i) => {
      const x = (i / (pts.length - 1)) * W;
      const y = H - ((p - min) / range) * (H - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }

  function handleClick(item) {
    if (item.watch && onSelectWatch) {
      onSelectWatch(item.watch);
    }
  }

  return (
    <div style={{ marginBottom: 28 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          width: 7, height: 7, borderRadius: '50%', background: '#0f9b6e',
          boxShadow: '0 0 6px #0f9b6e', flexShrink: 0,
          animation: 'pulse 2s ease-in-out infinite',
        }} />
        <span style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: 'var(--text3)',
          fontFamily: "'Space Mono', monospace",
        }}>Live market — click any card to view details</span>
      </div>

      {/* Scrolling ticker */}
      <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 8, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <div style={{ display: 'flex', gap: 10, minWidth: 'max-content' }}>
          {prices.map((item, i) => {
            const up = item.pct >= 0;
            const color = up ? '#0f9b6e' : '#c94f28';
            const pts = history[i]?.points;
            const clickable = !!item.watch;

            return (
              <div
                key={item.id}
                onClick={() => handleClick(item)}
                style={{
                  background: 'var(--surface)',
                  border: `0.5px solid ${up ? 'rgba(15,155,110,0.2)' : 'rgba(201,79,40,0.2)'}`,
                  borderRadius: 10,
                  padding: '10px 14px',
                  minWidth: 180,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => {
                  if (!clickable) return;
                  e.currentTarget.style.borderColor = color;
                  e.currentTarget.style.boxShadow = `0 6px 24px rgba(0,0,0,0.10)`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = up ? 'rgba(15,155,110,0.2)' : 'rgba(201,79,40,0.2)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'none';
                }}
              >
                {/* Sparkline background */}
                <svg width={W} height={H} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.3 }}>
                  <polyline points={sparkline(pts)} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                {/* Click to view hint */}
                {clickable && (
                  <div style={{
                    position: 'absolute', top: 6, right: 8,
                    fontSize: 8, color: color, fontFamily: "'Space Mono', monospace",
                    opacity: 0.7, letterSpacing: '0.05em',
                  }}>
                    VIEW →
                  </div>
                )}

                <div style={{
                  fontSize: 10, color: 'var(--text3)', fontFamily: "'Space Mono', monospace",
                  letterSpacing: '0.06em', marginBottom: 3, whiteSpace: 'nowrap',
                  maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {item.ref}
                </div>

                <div style={{
                  fontSize: 12, fontWeight: 600, color: 'var(--text)',
                  fontFamily: "'Space Grotesk', sans-serif",
                  whiteSpace: 'nowrap', maxWidth: 120,
                  overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4,
                }}>
                  {item.name}
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{
                    fontSize: 14, fontWeight: 700, color: 'var(--text)',
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}>
                    {fmt(item.price)}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, color,
                    fontFamily: "'Space Mono', monospace",
                  }}>
                    {up ? '▲' : '▼'} {Math.abs(item.pct).toFixed(2)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
